import React, { useState, useEffect, useRef } from "react";
import {
  motion,
  AnimatePresence,
  useScroll,
  useTransform,
} from "framer-motion";
import { toast } from "react-hot-toast";
import questionnaireService from "../services/questionnaireService";
import responseService from "../services/responseService";
import QuestionnaireSection from "./QuestionnaireSection";
import { FORM_STATUS } from "../models/types";

/**
 * DynamicQuestionnaire component
 * Client-facing questionnaire with timeline UI similar to the original
 */
const DynamicQuestionnaire = ({
  userId,
  onSubmit,
  onAnalyzeUrl,
  onComplete,
  websiteUrl,
  analysisResults,
  isAnalyzing,
  analyzeError,
}) => {
  const [questionnaire, setQuestionnaire] = useState(null);
  const [responseId, setResponseId] = useState(null);
  const [responses, setResponses] = useState({});
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [completedSections, setCompletedSections] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [height, setHeight] = useState(0);

  const containerRef = useRef(null);
  const timelineRef = useRef(null);

  // Add a ref to guard against double execution
  const hasLoaded = useRef(false);

  // Set up scroll animation
  useEffect(() => {
    if (timelineRef.current) {
      const rect = timelineRef.current.getBoundingClientRect();
      setHeight(rect.height);
    }
  }, [timelineRef, questionnaire]);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start 10%", "end 50%"],
  });

  const heightTransform = useTransform(scrollYProgress, [0, 1], [0, height]);
  const opacityTransform = useTransform(scrollYProgress, [0, 0.1], [0, 1]);

  // Load active questionnaire and initialize response
  useEffect(() => {
    if (hasLoaded.current) return;
    hasLoaded.current = true;
    async function loadQuestionnaire() {
      try {
        setLoading(true);
        console.log("Loading questionnaire for user:", userId);

        // Get the active questionnaire
        const activeQuestionnaire =
          await questionnaireService.getActiveQuestionnaire();

        if (!activeQuestionnaire) {
          console.warn("No active questionnaire found");
          setError(
            "No active questionnaire found. Please create and activate a questionnaire first."
          );
          setLoading(false);
          return;
        }

        console.log("Active questionnaire found:", activeQuestionnaire.id);
        setQuestionnaire(activeQuestionnaire);

        try {
          // Check if there's an existing response for this user
          console.log("Checking for existing responses for user:", userId);
          const existingResponse =
            await responseService.getLatestResponseForUser(userId);

          if (existingResponse) {
            console.log(
              "Found existing response, using it:",
              existingResponse.id,
              "Status:",
              existingResponse.status
            );
            setResponseId(existingResponse.id);
            setResponses(existingResponse.responses || {});

            // Determine completed sections
            const completed = [];
            activeQuestionnaire.sections.forEach((section, index) => {
              const sectionQuestions = section.questions || [];

              if (sectionQuestions.length === 0) {
                completed.push(index);
                return;
              }

              // Check if all required questions in this section have been answered
              const allRequiredAnswered = sectionQuestions
                .filter((q) => q.required)
                .every((q) => {
                  const response = existingResponse.responses[q.id];
                  return (
                    response && response.value !== null && response.value !== ""
                  );
                });

              if (allRequiredAnswered) {
                completed.push(index);
              }
            });

            setCompletedSections(completed);

            // Set current section to the first incomplete section
            const firstIncompleteSection =
              activeQuestionnaire.sections.findIndex(
                (_, index) => !completed.includes(index)
              );

            if (firstIncompleteSection !== -1) {
              setCurrentSectionIndex(firstIncompleteSection);
            }
          } else {
            // Create a new response ONLY if no response exists for this user
            console.log(
              "No existing response found. Starting new response for user:",
              userId
            );
            try {
              const newResponseId =
                await responseService.startQuestionnaireResponseForUser(
                  activeQuestionnaire.id,
                  userId,
                  activeQuestionnaire
                );

              console.log("Created new response:", newResponseId);
              setResponseId(newResponseId);
            } catch (startError) {
              console.error("Error starting new response:", startError);
              setError(
                "Failed to start a new questionnaire response. Please try again later."
              );
              setLoading(false);
              return;
            }
          }
        } catch (responseError) {
          console.error("Error handling response logic:", responseError);
          setError(
            "Failed to access questionnaire response data. Please try again later."
          );
          setLoading(false);
          return;
        }

        setLoading(false);
      } catch (error) {
        console.error("Error loading questionnaire:", error);
        setError(
          "Failed to load questionnaire. Please try again later. Error: " +
            error.message
        );
        setLoading(false);
      }
    }

    if (userId) {
      loadQuestionnaire();
    }
  }, [userId]);

  // Auto-validate section completion when a response is saved
  const autoValidateSection = (sectionIndex) => {
    // Don't re-validate already completed sections
    if (completedSections.includes(sectionIndex)) return;

    const section = questionnaire.sections[sectionIndex];
    const requiredQuestions = section.questions.filter((q) => q.required);

    // If no required questions, mark as complete
    if (requiredQuestions.length === 0) {
      if (!completedSections.includes(sectionIndex)) {
        setCompletedSections((prev) => [...prev, sectionIndex]);
      }
      return;
    }

    // Check if all required questions have answers
    const allRequiredAnswered = requiredQuestions.every((q) => {
      const response = responses[q.id];
      return response && response.value !== null && response.value !== "";
    });

    // If all required questions are answered, mark section as complete
    if (allRequiredAnswered && !completedSections.includes(sectionIndex)) {
      console.log(`Auto-marking section ${sectionIndex} as complete`);
      setCompletedSections((prev) => [...prev, sectionIndex]);
    }
  };

  // Handle saving a response to a question
  const handleSaveResponse = async (questionId, value) => {
    try {
      // Update local state
      setResponses((prev) => {
        const updated = {
          ...prev,
          [questionId]: {
            questionId,
            value,
            timestamp: new Date().toISOString(),
          },
        };
        console.log("Updated responses:", updated); // Debug log
        return updated;
      });

      // Find the section this question belongs to
      const sectionIndex = questionnaire.sections.findIndex((section) =>
        section.questions.some((q) => q.id === questionId)
      );

      // Auto-validate the section if we found it
      if (sectionIndex >= 0) {
        // We need to wait for the state update to complete
        setTimeout(() => autoValidateSection(sectionIndex), 0);
      }

      // Save to database
      await responseService.saveQuestionResponse(responseId, questionId, value);
    } catch (error) {
      console.error("Error saving response:", error);
      toast.error("Failed to save your response. Please try again.");
    }
  };

  // Handle section completion
  const handleSectionComplete = async (sectionIndex) => {
    if (!completedSections.includes(sectionIndex)) {
      setCompletedSections((prev) => [...prev, sectionIndex]);
    }

    // Move to next section if available
    if (sectionIndex < questionnaire.sections.length - 1) {
      const nextIndex = sectionIndex + 1;
      setCurrentSectionIndex(nextIndex);

      // Scroll to the next section
      const sectionElement = document.getElementById(`section-${nextIndex}`);
      if (sectionElement) {
        sectionElement.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  };

  // Handle questionnaire submission
  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      // Mark response as completed
      await responseService.submitResponse(responseId);

      // Transform data for brand profile if needed
      const formattedData = responseService.transformResponseForBrandProfile(
        { responses },
        questionnaire
      );

      // Call the onSubmit callback with the formatted data
      if (onSubmit) {
        await onSubmit(formattedData, responseId);
      }

      // Call the onComplete callback if provided
      if (onComplete) {
        onComplete();
      }

      toast.success("Questionnaire submitted successfully!");
      setIsSubmitting(false);
    } catch (error) {
      console.error("Error submitting questionnaire:", error);
      toast.error("Failed to submit questionnaire. Please try again.");
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!analysisResults || !questionnaire) return;

    // Find the Brand Info section (type === "brandInfo")
    const brandInfoSection = questionnaire.sections.find(
      (section) => section.type === "brandInfo"
    );
    if (!brandInfoSection) return;

    // Map analyzer results to question titles
    const mapping = {
      "What is your company name?": analysisResults.companyDetails?.name,
      "Briefly describe what your company does":
        analysisResults.companyDetails?.description,
      "What industry are you in?": analysisResults.companyDetails?.industry,
      "Logo URL": analysisResults.images?.[0]?.url,
      "Website URL": websiteUrl, // Use the analyzed URL prop instead
    };

    // First update local state
    setResponses((prev) => {
      let updated = { ...prev };
      let changed = false;

      brandInfoSection.questions.forEach((q) => {
        const value = mapping[q.title];
        if (
          value &&
          (!prev[q.id] || !prev[q.id].value) // Only prefill if empty
        ) {
          updated[q.id] = {
            questionId: q.id,
            value,
            timestamp: new Date().toISOString(),
            autoFilled: true,
          };
          changed = true;
        }
      });

      // Save colors/fonts/images as a special field for later use
      if (
        analysisResults.colors ||
        analysisResults.fonts ||
        analysisResults.images
      ) {
        updated["_brandStyle"] = {
          questionId: "_brandStyle",
          value: {
            colors: analysisResults.colors,
            fonts: analysisResults.fonts,
            images: analysisResults.images,
          },
          timestamp: new Date().toISOString(),
          autoFilled: true,
        };
        changed = true;
      }

      return changed ? updated : prev;
    });

    // Then save to Firestore
    const saveToFirestore = async () => {
      try {
        // Save regular responses
        for (const [questionId, response] of Object.entries(mapping)) {
          const question = brandInfoSection.questions.find(
            (q) => q.title === questionId
          );
          if (question && response) {
            await responseService.saveQuestionResponse(
              responseId,
              question.id,
              response
            );
          }
        }

        // Save _brandStyle data
        if (
          analysisResults.colors ||
          analysisResults.fonts ||
          analysisResults.images
        ) {
          await responseService.updateResponse(responseId, {
            brandStyle: {
              colors: analysisResults.colors,
              fonts: analysisResults.fonts,
              images: analysisResults.images,
              timestamp: new Date().toISOString(),
              autoFilled: true,
            },
          });
        }
      } catch (error) {
        console.error("Error saving analysis results:", error);
      }
    };

    if (responseId) {
      saveToFirestore();
    }
  }, [analysisResults, questionnaire, responseId, websiteUrl]);

  if (loading) {
    return (
      <div className="w-full bg-[#111111] dark:bg-[#111111] font-sans min-h-screen flex items-center justify-center">
        <div className="text-white text-lg">Loading questionnaire...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full bg-[#111111] dark:bg-[#111111] font-sans min-h-screen flex items-center justify-center">
        <div className="text-red-500 text-lg">{error}</div>
      </div>
    );
  }

  if (!questionnaire) {
    return (
      <div className="w-full bg-[#111111] dark:bg-[#111111] font-sans min-h-screen flex items-center justify-center">
        <div className="text-white text-lg">No questionnaire available</div>
      </div>
    );
  }

  return (
    <div
      className="w-full bg-[#111111] dark:bg-[#111111] font-sans"
      ref={containerRef}
    >
      <div ref={timelineRef} className="relative max-w-7xl mx-auto pb-20">
        {questionnaire.sections.map((section, index) => (
          <div
            key={section.id}
            id={`section-${index}`}
            className="flex justify-start pt-10 md:pt-40 md:gap-10"
          >
            <div className="sticky flex flex-col md:flex-row z-40 items-center top-40 self-start max-w-xs lg:max-w-sm md:w-full">
              <div className="h-10 absolute left-3 md:left-3 w-10 rounded-full bg-[#111111] flex items-center justify-center">
                <div
                  className={`h-4 w-4 rounded-full ${
                    completedSections.includes(index)
                      ? "bg-primary-green"
                      : currentSectionIndex === index
                      ? "bg-blue-500"
                      : "bg-neutral-700"
                  } border border-neutral-600 p-2`}
                />
              </div>
              <h3 className="hidden md:block text-xl md:pl-20 md:text-3xl font-bold text-white">
                {section.title}
              </h3>
            </div>

            <div className="relative pl-20 pr-4 md:pl-4 w-full">
              <h3 className="md:hidden block text-2xl mb-4 text-left font-bold text-white">
                {section.title}
              </h3>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="max-w-3xl"
              >
                <h4 className="text-xl md:text-2xl text-white mb-4">
                  {section.description}
                </h4>

                <div className="mt-8">
                  <QuestionnaireSection
                    section={section}
                    responses={responses}
                    onSaveResponse={handleSaveResponse}
                    isCurrentSection={currentSectionIndex === index}
                    isCompleted={completedSections.includes(index)}
                    websiteUrl={websiteUrl}
                    onAnalyzeUrl={onAnalyzeUrl}
                    analysisResults={analysisResults}
                    isAnalyzing={isAnalyzing}
                    analyzeError={analyzeError}
                  />
                </div>

                {/* Navigation buttons */}
                <div className="flex justify-between mt-12">
                  {index > 0 && (
                    <motion.button
                      whileHover={{ scale: 1.05, x: -5 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setCurrentSectionIndex(index - 1)}
                      className="py-3 px-6 rounded-full bg-transparent text-white border border-white/20 hover:border-white/40 transition-all duration-300"
                      disabled={isSubmitting}
                    >
                      Previous
                    </motion.button>
                  )}

                  {index === questionnaire.sections.length - 1 ? (
                    <>
                      {!completedSections.includes(index) && (
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => handleSectionComplete(index)}
                          className="py-3 px-8 mr-4 rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-all duration-300 font-medium"
                        >
                          Complete Section
                        </motion.button>
                      )}
                      <motion.button
                        whileHover={{ scale: 1.05, x: 5 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={handleSubmit}
                        disabled={
                          isSubmitting || !completedSections.includes(index)
                        }
                        className={`py-3 px-8 rounded-full bg-primary-green text-black transition-all duration-300 font-medium flex items-center gap-2
                          ${
                            isSubmitting || !completedSections.includes(index)
                              ? "opacity-50 cursor-not-allowed"
                              : "hover:bg-primary-green/90"
                          }`}
                      >
                        <span>{isSubmitting ? "Submitting..." : "Submit"}</span>
                        {isSubmitting && (
                          <svg
                            className="animate-spin h-4 w-4"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                        )}
                      </motion.button>
                    </>
                  ) : (
                    <motion.button
                      whileHover={{ scale: 1.05, x: 5 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleSectionComplete(index)}
                      disabled={isSubmitting}
                      className="py-3 px-8 rounded-full bg-primary-green text-black hover:bg-primary-green/90 transition-all duration-300 font-medium"
                    >
                      Next
                    </motion.button>
                  )}
                </div>
              </motion.div>
            </div>
          </div>
        ))}

        {/* Timeline bar */}
        <div
          style={{
            height: height + "px",
          }}
          className="absolute md:left-8 left-8 top-0 overflow-hidden w-[2px] bg-[linear-gradient(to_bottom,var(--tw-gradient-stops))] from-transparent from-[0%] via-neutral-700 to-transparent to-[99%] [mask-image:linear-gradient(to_bottom,transparent_0%,black_10%,black_90%,transparent_100%)]"
        >
          <motion.div
            style={{
              height: heightTransform,
              opacity: opacityTransform,
            }}
            className="absolute inset-x-0 top-0 w-[2px] bg-gradient-to-t from-primary-green via-blue-500 to-transparent from-[0%] via-[10%] rounded-full"
          />
        </div>
      </div>
    </div>
  );
};

export default DynamicQuestionnaire;
